import React from 'react';
import { Helmet } from 'react-helmet';
import { 
    CalendarDaysIcon,
    ClockIcon,
    UserGroupIcon,
    CheckCircleIcon,
    SparklesIcon,
    ArrowRightIcon
} from '@heroicons/react/24/outline';

const OkRdv = () => {
  const features = [
    {
      icon: CalendarDaysIcon,
      title: "Gestion de rendez-vous simplifiée",
      description: "Planifiez et gérez tous vos rendez-vous en quelques clics. Interface intuitive et rapide pour une prise de rendez-vous en moins d'une minute."
    },
    {
      icon: ClockIcon,
      title: "Gain de temps considérable",
      description: "Fini les échanges de mails interminables pour trouver un créneau. Vos clients réservent directement en ligne selon vos disponibilités."
    },
    {
      icon: UserGroupIcon,
      title: "Pour tous les professionnels",
      description: "Médecins, avocats, coachs, consultants, esthéticiens... OK RDV s'adapte à tous les métiers nécessitant une gestion de rendez-vous."
    },
    {
      icon: CheckCircleIcon,
      title: "Confirmation automatique",
      description: "Envoi automatique de confirmations par email et SMS. Vos clients sont toujours informés de leurs rendez-vous."
    }
  ];

  const benefits = [
    "Réduisez les rendez-vous manqués grâce aux rappels automatiques",
    "Optimisez votre planning en temps réel",
    "Acceptez les réservations 24/7 même en dehors de vos heures d'ouverture",
    "Gérez plusieurs praticiens et lieux depuis une seule interface"
  ];

  const useCases = [
    {
      title: "Professionnels de santé",
      description: "Médecins, dentistes, kinésithérapeutes, psychologues... Gérez vos consultations facilement."
    },
    {
      title: "Services aux particuliers",
      description: "Coiffeurs, esthéticiens, massages, soins du corps... Offrez une expérience client premium."
    },
    {
      title: "Conseil et coaching",
      description: "Avocats, consultants, coachs professionnels... Organisez vos entretiens efficacement."
    },
    {
      title: "Services techniques",
      description: "Plombiers, électriciens, réparateurs... Planifiez vos interventions sur le terrain."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50 pt-16">
      <Helmet>
        <title>OK RDV - Solution de prise de rendez-vous en ligne | Présentation</title>
        <meta name="description" content="Découvrez OK RDV, la solution de gestion de rendez-vous en ligne pour tous les professionnels. Planification simplifiée, confirmations automatiques, gain de temps garanti." />
        <meta name="keywords" content="prise de rendez-vous, gestion rendez-vous, réservation en ligne, planning professionnel, rendez-vous automatique" />
        <link rel="canonical" href="https://www.cv-analyse.fr/okrdv" />
      </Helmet>

      {/* Header Hero */}
      <div className="bg-gradient-to-r from-teal-600 via-cyan-600 to-blue-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-16">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-3xl mb-6 backdrop-blur-sm">
              <CalendarDaysIcon className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              OK RDV
            </h1>
            <p className="text-xl md:text-2xl text-white max-w-3xl mx-auto leading-relaxed font-medium">
              La solution de prise de rendez-vous en ligne qui simplifie votre quotidien professionnel
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://www.okrdv.eu/"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative inline-flex items-center justify-center px-10 py-5 bg-white text-teal-600 font-bold rounded-2xl hover:bg-teal-50 transition-all duration-300 shadow-2xl hover:shadow-3xl transform hover:-translate-y-2 hover:scale-105 text-lg"
              >
                <span>Découvrir OK RDV</span>
                <ArrowRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 transform -skew-x-12"></div>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Mission Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-100 rounded-full mb-4">
              <SparklesIcon className="w-8 h-8 text-teal-600" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Pourquoi OK RDV ?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              OK RDV est né d'un constat simple : la gestion des rendez-vous ne devrait pas être une source de stress. 
              Nous avons créé une solution intuitive, fiable et adaptée à tous les professionnels qui souhaitent optimiser leur organisation.
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gradient-to-br from-teal-50 to-cyan-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Fonctionnalités principales
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Tout ce dont vous avez besoin pour gérer vos rendez-vous efficacement
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div key={index} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 transform hover:-translate-y-2">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-r from-teal-500 to-cyan-600 rounded-xl mb-4">
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Pour qui ?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              OK RDV s'adapte à tous les professionnels qui gèrent des rendez-vous
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {useCases.map((useCase, index) => (
              <div key={index} className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-6 border border-teal-100 hover:shadow-lg transition-shadow duration-300">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {useCase.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {useCase.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gradient-to-r from-teal-600 to-cyan-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Les avantages OK RDV
          </h2>
          <p className="text-xl text-teal-100 mb-8 max-w-3xl mx-auto leading-relaxed">
            Une solution complète pour transformer votre gestion de rendez-vous
          </p>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white bg-opacity-10 backdrop-blur-sm rounded-xl p-6 border border-white border-opacity-20 hover:bg-opacity-20 transition-all duration-300">
                <div className="flex items-start">
                  <CheckCircleIcon className="w-6 h-6 text-white mr-3 flex-shrink-0 mt-1" />
                  <p className="text-white text-lg text-left">{benefit}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-gradient-to-r from-teal-50 to-cyan-50 rounded-2xl p-8 md:p-12 border border-teal-100 shadow-xl">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-teal-500 to-cyan-600 rounded-full mb-6">
              <CalendarDaysIcon className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Prêt à simplifier votre gestion de rendez-vous ?
            </h2>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed max-w-2xl mx-auto">
              Rejoignez les professionnels qui ont choisi OK RDV pour optimiser leur organisation et améliorer l'expérience de leurs clients.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://www.okrdv.eu/"
                target="_blank"
                rel="noopener noreferrer"
                className="group relative inline-flex items-center justify-center px-10 py-5 bg-gradient-to-r from-teal-600 to-cyan-600 text-white font-bold rounded-2xl hover:from-teal-700 hover:to-cyan-700 transition-all duration-300 shadow-2xl hover:shadow-3xl transform hover:-translate-y-2 hover:scale-105 text-lg"
              >
                <span>Essayer OK RDV gratuitement</span>
                <ArrowRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                
                {/* Effet de brillance au survol */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 transform -skew-x-12"></div>
                
                {/* Overlay au survol */}
                <div className="absolute inset-0 bg-white/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity"></div>
              </a>
            </div>
            <p className="text-sm text-gray-500 mt-6">
              ✨ Essai gratuit disponible - Aucune carte bancaire requise
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OkRdv;

